"use client";

import { AboutTabContent } from "@/components/event-detail/guess-expand-tabs/AboutTabContent";
import { QuestionIcon } from "@/components/event-detail/question-icon";
import { OrderBookModal } from "@/components/platform/order-book-modal";
import EmptyState from "@/components/portfolio/empty-state";
import { ExpandCard } from "@/components/ui/expandCard";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SelectionIndicator } from "@/components/ui/selection-indicator";
import { cn } from "@/lib/utils";
import { useEventConnect } from "@/store/eventConnect";
import { formatMonetaryValue, formatUnitPrice } from "@/utils/utils";
import { BigMath, getTraditionalOrderBookData, type TraditionalOrderBookSide } from "@yn/utils";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

type OrderBookViewType = "maker" | "traditional";
type MakerOrderBookTab = "orderBook" | "about";
type TraditionalOrderBookTab = TraditionalOrderBookSide | "about";

interface OrderBookDisplayOrder {
    price: number;
    totalQuantity: number;
    totalAmount: number;
}

interface TraditionalOrderBookDisplayData {
    yesSellOrders: OrderBookDisplayOrder[];
    noSellOrders: OrderBookDisplayOrder[];
    yesBuyOrders: OrderBookDisplayOrder[];
    noBuyOrders: OrderBookDisplayOrder[];
}

interface OrderBookRowProps {
    price: number;
    quantity: number;
    type: "yes" | "no";
    maxQuantity: number;
}

const ORDER_BOOK_TYPE_OPTIONS: Array<{ label: string; value: OrderBookViewType }> = [ {
    label: "Traditional",
    value: "traditional",
}, { label: "Only maker", value: "maker" } ];

const MAKER_ORDER_BOOK_TITLE_OPTIONS: Array<{ label: string; value: MakerOrderBookTab }> = [ {
    label: "Order Book",
    value: "orderBook",
}, { label: "About", value: "about" } ];

const EMPTY_ORDER_BOOK_ORDER: OrderBookDisplayOrder = { price: 0, totalQuantity: 0, totalAmount: 0 };
const REFRESH_SPIN_MS = 1000;

const DEPTH_WIDTH_CLASS_BY_STEP: Record<number, string> = {
    0: "w-0",
    5: "w-[5%]",
    10: "w-[10%]",
    15: "w-[15%]",
    20: "w-[20%]",
    25: "w-1/4",
    30: "w-[30%]",
    35: "w-[35%]",
    40: "w-[40%]",
    45: "w-[45%]",
    50: "w-1/2",
    55: "w-[55%]",
    60: "w-[60%]",
    65: "w-[65%]",
    70: "w-[70%]",
    75: "w-3/4",
    80: "w-4/5",
    85: "w-[85%]",
    90: "w-[90%]",
    95: "w-[95%]",
    100: "w-full",
};

function getDepthWidthClass( depthPercent: number ) {
    const normalizedDepth = Number.isFinite( depthPercent ) ? Math.min( 100, Math.max( 0, depthPercent ) ) : 0;
    const roundedStep = Math.min( 100, Math.max( 0, Math.round( normalizedDepth / 5 ) * 5 ) );

    return DEPTH_WIDTH_CLASS_BY_STEP[roundedStep] ?? "w-0";
}

function OrderBookRow( { price, quantity, type, maxQuantity }: OrderBookRowProps ) {
    const isYes = type === "yes";
    const widthPercent = BigMath.gt( maxQuantity, 0 ) ? BigMath.times( BigMath.div( quantity, maxQuantity ), 100 ) : 0;
    const barColor = isYes ? "bg-yes-subtle" : "bg-no-subtle";

    return (
        <div className="relative flex-shrink-0 flex items-center h-7 px-2 border-b border-[rgba(48,161,89,0.10)] last:border-0 w-full">
            <div
                className={cn(
                    "absolute top-0 bottom-[-0.5px] right-0 z-0 transition-all duration-300",
                    barColor,
                    getDepthWidthClass( widthPercent ),
                )}
            />

            <div className="relative z-10 flex items-center justify-between w-full">
                <span className="text-xs font-normal text-t3">{formatUnitPrice( price )}</span>
                <span className="text-t2 text-xs font-normal">{quantity.toLocaleString()}</span>
            </div>
        </div>
    );
}

const getTotalQuantity = ( orders: OrderBookDisplayOrder[] ) => {
    return orders.reduce( ( total, order ) => BigMath.plus( total, order.totalQuantity ), 0 );
};

/** Refresh icon for Order Book header, shared by order-book / guess-order-book / main-guess-card */
export function RefreshOrderBookIcon( { className }: { className?: string } ) {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <path
                d="M6.94727 11.1504C7.34272 11.1504 7.6737 11.4205 7.76855 11.7861C7.7866 11.8551 7.79688 11.9273 7.79688 12.002C7.79693 14.3229 9.67899 16.2041 12 16.2041C13.1338 16.2041 14.1612 15.7566 14.918 15.0264C15.2557 14.7004 15.7941 14.7093 16.1201 15.0469C16.4462 15.3846 16.4364 15.923 16.0986 16.249C15.0377 17.273 13.5918 17.9043 12 17.9043C10.3546 17.9043 8.86726 17.2305 7.79688 16.1445V16.4912C7.79688 16.9607 7.41671 17.3408 6.94727 17.3408C6.47782 17.3408 6.09766 16.9607 6.09766 16.4912V12C6.09766 11.5306 6.47782 11.1504 6.94727 11.1504ZM12 6.09961C13.6451 6.09964 15.1328 6.77276 16.2031 7.8584V7.50781C16.2031 7.03837 16.5833 6.6582 17.0527 6.6582C17.5222 6.6582 17.9023 7.03837 17.9023 7.50781V11.999C17.9023 12.0443 17.8974 12.0885 17.8906 12.1318C17.828 12.5393 17.4778 12.8515 17.0527 12.8516C16.5833 12.8516 16.2022 12.4713 16.2021 12.002C16.2021 9.68095 14.321 7.79888 12 7.79883C10.8126 7.79883 9.74091 8.29025 8.97559 9.08301C8.64961 9.42069 8.11217 9.43035 7.77441 9.10449C7.43667 8.77845 7.42689 8.24009 7.75293 7.90234C8.82533 6.79159 10.3326 6.09961 12 6.09961Z"
                fill="var(--ui-content-t3)"
            />
        </svg>
    );
}

function OrderBookTypeIcon( { className }: { className?: string } ) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className={className}
        >
            <path
                d="M8 10.5C9.38071 10.5 10.5 9.38071 10.5 8C10.5 6.61929 9.38071 5.5 8 5.5C6.61929 5.5 5.5 6.61929 5.5 8C5.5 9.38071 6.61929 10.5 8 10.5Z"
                stroke="#7A7A83"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M8.12814 12.8819C8.04439 12.8819 7.96002 12.8819 7.87814 12.8819L5.87502 14C5.09522 13.7377 4.37194 13.3306 3.74314 12.8L3.73564 10.55C3.69127 10.48 3.64939 10.4094 3.61064 10.3369L1.61877 9.2025C1.46188 8.40893 1.46188 7.59232 1.61877 6.79875L3.60877 5.6675C3.64939 5.59563 3.69127 5.52437 3.73377 5.45437L3.74377 3.20438C4.37199 2.67228 5.09509 2.26365 5.87502 2L7.87502 3.11812C7.95877 3.11812 8.04314 3.11812 8.12502 3.11812L10.125 2C10.9048 2.2623 11.6281 2.66943 12.2569 3.2L12.2644 5.45C12.3088 5.52 12.3506 5.59063 12.3894 5.66313L14.38 6.79688C14.5369 7.59044 14.5369 8.40706 14.38 9.20063L12.39 10.3319C12.3494 10.4037 12.3075 10.475 12.265 10.545L12.255 12.795C11.6272 13.3272 10.9045 13.736 10.125 14L8.12814 12.8819Z"
                stroke="#7A7A83"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export function OrderBook(
    { guessInfo, eventNo, modal, defaultExpanded = false, className }: {
        guessInfo: any;
        eventNo?: string | null;
        modal?: boolean;
        defaultExpanded?: boolean;
        className?: string;
    },
) {
    const { loadSnapshot } = useEventConnect();
    const subscribeGuess = useEventConnect( ( state ) => state.subscribeGuess );
    const unsubscribeGuess = useEventConnect( ( state ) => state.unsubscribeGuess );
    const [ isOrderBookModalOpen, setIsOrderBookModalOpen ] = useState( false );
    const [ orderBookType, setOrderBookType ] = useState<OrderBookViewType>( "traditional" );
    const [ traditionalTab, setTraditionalTab ] = useState<TraditionalOrderBookTab>( "yes" );
    const [ makerTab, setMakerTab ] = useState<MakerOrderBookTab>( "orderBook" );
    const [ isOrderBookExpanded, setIsOrderBookExpanded ] = useState( defaultExpanded );
    const [ isRefreshing, setIsRefreshing ] = useState( false );
    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>( undefined );
    const guessNo = guessInfo?.guessNo;
    const isAboutTab = orderBookType === "traditional" ? traditionalTab === "about" : makerTab === "about";
    const traditionalTabOptions = useMemo<Array<{ label: string; value: TraditionalOrderBookTab }>>(
        () => [
            { label: guessInfo?.yesDesc || "Yes", value: "yes" },
            { label: guessInfo?.noDesc || "No", value: "no" },
            { label: "About", value: "about" },
        ],
        [ guessInfo?.noDesc, guessInfo?.yesDesc ],
    );

    const handleRefresh = () => {
        if ( !guessNo || isRefreshing ) {
            return;
        }

        if ( refreshTimeoutRef.current ) {
            clearTimeout( refreshTimeoutRef.current );
        }

        setIsRefreshing( true );
        loadSnapshot( guessNo, { force: true } );
        refreshTimeoutRef.current = setTimeout( () => {
            refreshTimeoutRef.current = undefined;
            setIsRefreshing( false );
        }, REFRESH_SPIN_MS );
    };

    useEffect( () => () => {
        if ( refreshTimeoutRef.current ) {
            clearTimeout( refreshTimeoutRef.current );
        }
    }, [] );

    useEffect( () => {
        if ( !eventNo || !guessNo ) {
            return;
        }

        subscribeGuess( eventNo, guessNo );

        return () => {
            unsubscribeGuess( eventNo, guessNo );
        };
    }, [ eventNo, guessNo, subscribeGuess, unsubscribeGuess ] );

    return (
        <>
            <ExpandCard
                defaultExpanded={defaultExpanded}
                onExpandChange={( isCollapsed ) => {
                    setIsOrderBookExpanded( !isCollapsed );

                    if ( !isCollapsed && guessInfo && !modal ) {
                        loadSnapshot( guessInfo.guessNo );
                    }
                }}
                title={orderBookType === "traditional"
                    ? (
                        <OrderBookHeaderTabs
                            value={traditionalTab}
                            options={traditionalTabOptions}
                            onChange={setTraditionalTab}
                            showActiveUnderline={isOrderBookExpanded}
                            shouldExpandOnSelect={!isOrderBookExpanded}
                            className={isOrderBookExpanded ? undefined : "h-[30px]"}
                        />
                    )
                    : (
                        <OrderBookHeaderTabs
                            value={makerTab}
                            options={MAKER_ORDER_BOOK_TITLE_OPTIONS}
                            onChange={setMakerTab}
                            showActiveUnderline={isOrderBookExpanded}
                            shouldExpandOnSelect={!isOrderBookExpanded}
                            className={isOrderBookExpanded ? undefined : "h-[30px]"}
                        />
                    )}
                rightActions={isAboutTab ? null : (
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={( e ) => {
                                e.stopPropagation();
                                handleRefresh();
                            }}
                            disabled={isRefreshing}
                            aria-label="Refresh order book"
                            className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center size-6 text-t3 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            <RefreshOrderBookIcon className={cn( "size-6", isRefreshing ? "animate-spin" : "" )} />
                        </button>
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={( e ) => {
                                e.stopPropagation();
                                setIsOrderBookModalOpen( true );
                            }}
                            onKeyDown={( e ) => {
                                if ( e.key === "Enter" || e.key === " " ) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setIsOrderBookModalOpen( true );
                                }
                            }}
                            aria-label="Order book basics"
                        >
                            <QuestionIcon className="w-6 h-4 text-t3" />
                        </span>
                        <OrderBookTypeSelector value={orderBookType} onChange={setOrderBookType} />
                    </div>
                )}
                className={cn(
                    "box-border border border-stroke rounded-xl p-3 transition-all duration-300",
                    !isOrderBookExpanded && "py-2",
                    className,
                )}
            >
                <OrderBookTabContent
                    guessInfo={guessInfo}
                    orderBookType={orderBookType}
                    traditionalTab={traditionalTab}
                    traditionalTabOptions={traditionalTabOptions}
                    makerTab={makerTab}
                />
            </ExpandCard>
            <OrderBookModal isOpen={isOrderBookModalOpen} onClose={() => setIsOrderBookModalOpen( false )} />
        </>
    );
}

function OrderBookHeaderTabs<T extends string>(
    { value, options, onChange, showActiveUnderline = true, shouldExpandOnSelect = false, className }: {
        value: T;
        options: Array<{ label: string; value: T }>;
        onChange: ( value: T ) => void;
        showActiveUnderline?: boolean;
        shouldExpandOnSelect?: boolean;
        className?: string;
    },
) {
    return (
        <div className={cn( "flex items-center gap-4 h-8", className )}>
            {options.map( ( option ) => {
                const isActive = value === option.value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        aria-pressed={isActive}
                        onClick={( e ) => {
                            if ( !shouldExpandOnSelect ) {
                                e.stopPropagation();
                            }

                            onChange( option.value );
                        }}
                        className={cn(
                            "relative h-full text-sm font-medium transition-colors",
                            isActive ? "text-t1 font-bold" : "text-t3",
                        )}
                    >
                        {option.label}
                        {isActive && showActiveUnderline
                            ? <span className="absolute bottom-0 left-0 h-0.5 w-full bg-content-primary" />
                            : null}
                    </button>
                );
            } )}
        </div>
    );
}

function OrderBookTypeSelector(
    { value, onChange }: { value: OrderBookViewType; onChange: ( value: OrderBookViewType ) => void },
) {
    const [ open, setOpen ] = useState( false );

    const handleSelect = ( nextValue: OrderBookViewType ) => {
        onChange( nextValue );
        setOpen( false );
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    aria-label="Select order book type"
                    onClick={( e ) => e.stopPropagation()}
                    className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center size-6 text-t3"
                >
                    <OrderBookTypeIcon className="size-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                sideOffset={8}
                onClick={( e ) => e.stopPropagation()}
                className="w-[220px] rounded-xl bg-brand-button border-0 p-1 shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1),0px_8px_16px_0px_rgba(0,0,0,0.1)]"
            >
                <div className="flex flex-col">
                    {ORDER_BOOK_TYPE_OPTIONS.map( ( option ) => {
                        const isActive = value === option.value;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                onClick={( e ) => {
                                    e.stopPropagation();
                                    handleSelect( option.value );
                                }}
                                className={cn(
                                    "flex items-center gap-3 rounded-lg px-2 py-3 text-left text-sm transition-colors focus:outline-none",
                                    isActive ? "font-medium text-t1" : "font-medium text-t1",
                                )}
                            >
                                <SelectionIndicator selected={isActive} className="shrink-0" />
                                <span>{option.label}</span>
                            </button>
                        );
                    } )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

function MakerOrderBookContent(
    { guessInfo, sellData }: {
        guessInfo: any;
        sellData: { yesSellOrders: OrderBookDisplayOrder[]; noSellOrders: OrderBookDisplayOrder[] };
    },
) {
    const yesMaxQty = getTotalQuantity( sellData.yesSellOrders );
    const noMaxQty = getTotalQuantity( sellData.noSellOrders );

    if ( sellData.yesSellOrders.length === 0 && sellData.noSellOrders.length === 0 ) {
        return (
            <EmptyState
                text="No open orders"
                className="h-[100px] gap-2"
                iconClassName="size-8"
                textClassName="text-sm"
            />
        );
    }

    return (
        <>
            <div className="flex w-full border-b border-stroke gap-2 pt-1 mt-1 pb-2">
                <div className="flex flex-1 items-center justify-between">
                    <span className="text-t1 text-xs font-medium capitalize">price</span>
                    <div className="flex items-center gap-1">
                        <span className="text-t1 text-xs font-medium">Qty at</span>
                        <span className="text-action-accent text-xs font-semibold truncate max-w-15">
                            {guessInfo?.yesDesc || "Yes"}
                        </span>
                    </div>
                </div>

                <div className="flex flex-1 items-center justify-between">
                    <span className="text-t1 text-xs font-medium capitalize">price</span>
                    <div className="flex items-center gap-1">
                        <span className="text-t1 text-xs font-medium">Qty at</span>
                        <span className="text-state-danger text-xs font-semibold truncate max-w-15">
                            {guessInfo?.noDesc || "No"}
                        </span>
                    </div>
                </div>
            </div>
            <div className="flex w-full gap-2">
                <div className="flex flex-col flex-1">
                    {sellData.yesSellOrders.map( ( order, index ) => (
                        <OrderBookRow
                            key={`yes-${index}`}
                            price={Number( order.price )}
                            quantity={order.totalQuantity}
                            type="yes"
                            maxQuantity={yesMaxQty}
                        />
                    ) )}
                </div>

                <div className="flex flex-col flex-1">
                    {sellData.noSellOrders.map( ( order, index ) => (
                        <OrderBookRow
                            key={`no-${index}`}
                            price={Number( order.price )}
                            quantity={order.totalQuantity}
                            type="no"
                            maxQuantity={noMaxQty}
                        />
                    ) )}
                </div>
            </div>
        </>
    );
}

function TraditionalOrderBookRow(
    { label, price, quantity, totalAmount, depthPercent, tone }: {
        label?: string;
        price: number;
        quantity: number;
        totalAmount: number;
        depthPercent: number;
        tone: "yes" | "no";
    },
) {
    const isYes = tone === "yes";

    return (
        <div className="relative grid h-7 grid-cols-[minmax(40px,1fr)_44px_minmax(0,1fr)_minmax(0,1fr)] items-center gap-2 overflow-hidden px-2 text-xs">
            <div
                className={cn(
                    "absolute left-0 top-0 bottom-0 z-0 transition-all duration-300",
                    isYes ? "bg-yes-subtle" : "bg-no-subtle",
                    getDepthWidthClass( depthPercent ),
                )}
            />
            <span className={cn( "relative z-10 font-medium", isYes ? "text-yes" : "text-no" )}>{label ?? ""}</span>
            <span className={cn( "relative z-10 text-right font-medium", isYes ? "text-yes" : "text-no" )}>
                {formatUnitPrice( price, { precision: 0 } )}
            </span>
            <span className="relative z-10 min-w-0 text-right text-t1">{quantity.toLocaleString()}</span>
            <span className="relative z-10 min-w-0 text-right text-t1">
                {formatMonetaryValue( totalAmount, { useScale: false, decimalDisplay: "max", maxPrecision: 2 } )}
            </span>
        </div>
    );
}

function TraditionalOrderBookContent(
    { guessInfo, orderData, traditionalSide, traditionalSideOptions }: {
        guessInfo: any;
        orderData: TraditionalOrderBookDisplayData;
        traditionalSide: TraditionalOrderBookSide;
        traditionalSideOptions: Array<{ label: string; value: TraditionalOrderBookSide }>;
    },
) {
    const guessNo = guessInfo?.guessNo;
    const marketPrice = useEventConnect( ( state ) => ( guessNo ? state.guesses[guessNo] : undefined ) );
    const yesPrice = marketPrice?.yesPrice ?? guessInfo?.yesRealtimePrice;
    const noPrice = marketPrice?.noPrice ?? guessInfo?.noRealtimePrice;

    const traditionalData = useMemo( () => {
        return getTraditionalOrderBookData( {
            side: traditionalSide,
            yesSellOrders: orderData.yesSellOrders,
            noSellOrders: orderData.noSellOrders,
            yesBuyOrders: orderData.yesBuyOrders,
            noBuyOrders: orderData.noBuyOrders,
            yesPrice,
            noPrice,
        } );
    }, [
        noPrice,
        orderData.noBuyOrders,
        orderData.noSellOrders,
        orderData.yesBuyOrders,
        orderData.yesSellOrders,
        traditionalSide,
        yesPrice,
    ] );
    const sideLabel = traditionalSideOptions.find( ( option ) => option.value === traditionalSide )?.label
        || ( traditionalSide === "yes" ? "Yes" : "No" );
    const activeTone = traditionalSide === "yes" ? "yes" : "no";

    return (
        <div className="flex flex-col overflow-hidden">
            <div className="flex  w-full flex-col overflow-hidden">
                <div className="flex flex-col gap-px mt-1.5">
                    {traditionalData.asks.map( ( order, index ) => (
                        <TraditionalOrderBookRow
                            key={`ask-${index}`}
                            label={index === traditionalData.asks.length - 1 ? "Asks" : undefined}
                            price={order.price}
                            quantity={order.quantity}
                            totalAmount={order.totalAmount}
                            depthPercent={order.depthPercent}
                            tone="no"
                        />
                    ) )}
                </div>

                <div className="flex h-7 shrink-0 items-center justify-center gap-3 border-y border-stroke px-2">
                    <span className={cn( "text-sm font-medium", activeTone === "yes" ? "text-yes" : "text-no" )}>
                        {sideLabel}
                    </span>
                    <span className={cn( "text-xs", activeTone === "yes" ? "text-yes" : "text-no" )}>
                        Last {formatUnitPrice( traditionalData.lastPrice, { precision: 0 } )}
                    </span>
                </div>

                <div className="flex flex-col gap-px mt-px">
                    {traditionalData.bids.map( ( order, index ) => (
                        <TraditionalOrderBookRow
                            key={`bid-${index}`}
                            label={index === 0 ? "Bids" : undefined}
                            price={order.price}
                            quantity={order.quantity}
                            totalAmount={order.totalAmount}
                            depthPercent={order.depthPercent}
                            tone="yes"
                        />
                    ) )}
                </div>
            </div>
        </div>
    );
}

export function OrderBookTabContent(
    { guessInfo, orderBookType = "maker", traditionalTab = "yes", traditionalTabOptions, makerTab = "orderBook" }: {
        guessInfo: any;
        orderBookType?: OrderBookViewType;
        traditionalTab?: TraditionalOrderBookTab;
        traditionalTabOptions?: Array<{ label: string; value: TraditionalOrderBookTab }>;
        makerTab?: MakerOrderBookTab;
    },
) {
    const orderbookData = useEventConnect( ( state ) => state.orderbookData );

    const orderData = useMemo( () => {
        if ( !orderbookData || !guessInfo ) {
            return { yesSellOrders: [], noSellOrders: [], yesBuyOrders: [], noBuyOrders: [] };
        }
        const rawYesSellOrders = orderbookData[guessInfo.guessNo]?.[1]?.sellOrders || [];
        const rawNoSellOrders = orderbookData[guessInfo.guessNo]?.[2]?.sellOrders || [];
        const rawYesBuyOrders = orderbookData[guessInfo.guessNo]?.[1]?.buyOrders || [];
        const rawNoBuyOrders = orderbookData[guessInfo.guessNo]?.[2]?.buyOrders || [];
        const yesSellOrders = [
            ...rawYesSellOrders.slice( 0, 5 ),
            ...Array( Math.max( 0, 5 - rawYesSellOrders.length ) ).fill( EMPTY_ORDER_BOOK_ORDER ),
        ];
        const noSellOrders = [
            ...rawNoSellOrders.slice( 0, 5 ),
            ...Array( Math.max( 0, 5 - rawNoSellOrders.length ) ).fill( EMPTY_ORDER_BOOK_ORDER ),
        ];
        const yesBuyOrders = [
            ...rawYesBuyOrders.slice( 0, 5 ),
            ...Array( Math.max( 0, 5 - rawYesBuyOrders.length ) ).fill( EMPTY_ORDER_BOOK_ORDER ),
        ];
        const noBuyOrders = [
            ...rawNoBuyOrders.slice( 0, 5 ),
            ...Array( Math.max( 0, 5 - rawNoBuyOrders.length ) ).fill( EMPTY_ORDER_BOOK_ORDER ),
        ];

        return { yesSellOrders, noSellOrders, yesBuyOrders, noBuyOrders };
    }, [ orderbookData, guessInfo ] );

    let content: ReactNode;

    if ( orderBookType === "traditional" ) {
        if ( traditionalTab === "about" ) {
            content = <AboutTabContent guess={guessInfo} event={guessInfo} />;
        } else {
            const traditionalSideOptions = traditionalTabOptions?.filter( (
                option,
            ): option is { label: string; value: TraditionalOrderBookSide } => option.value !== "about" );

            content = (
                <TraditionalOrderBookContent
                    guessInfo={guessInfo}
                    orderData={orderData}
                    traditionalSide={traditionalTab}
                    traditionalSideOptions={traditionalSideOptions
                        || [ { label: guessInfo?.yesDesc || "Yes", value: "yes" }, {
                            label: guessInfo?.noDesc || "No",
                            value: "no",
                        } ]}
                />
            );
        }
    } else if ( makerTab === "about" ) {
        content = <AboutTabContent guess={guessInfo} event={guessInfo} />;
    } else {
        content = <MakerOrderBookContent guessInfo={guessInfo} sellData={orderData} />;
    }

    return (
        <div className="flex flex-col w-full gap-2 animate-in slide-in-from-top-1 fade-in duration-200">{content}</div>
    );
}
